import type { Library } from '../library';
import type { Player } from '../player';
import { fmtTime, h, ICON } from './dom';

/** 播单抽屉：播单切换 + 播单内曲目选择 */
export class PlaylistDrawer {
  readonly el: HTMLElement;
  private tabs: HTMLElement;
  private list: HTMLElement;
  private viewId = '';

  constructor(
    private readonly lib: Library,
    private readonly player: Player,
    private readonly onImport: () => void,
  ) {
    this.tabs = h('div', { class: 'pl-tabs', role: 'tablist' });
    this.list = h('ol', { class: 'pl-list' });
    this.el = h(
      'aside',
      { id: 'drawer', class: 'drawer', 'aria-label': '播单' },
      h(
        'div',
        { class: 'panel-head' },
        h('div', { class: 'panel-title' }, '播单'),
        h('button', { class: 'icon-btn', title: '关闭', html: ICON.close, onclick: () => this.toggle(false) }),
      ),
      this.tabs,
      this.list,
      h(
        'div',
        { class: 'drawer-foot' },
        h('button', { class: 'btn', onclick: () => this.onImport() }, h('span', { class: 'ico', html: ICON.folder }), ' 导入本地音频'),
      ),
    );
    this.el.addEventListener('pointerdown', (e) => e.stopPropagation());
    player.addEventListener('track', () => this.render());
  }

  get open(): boolean {
    return this.el.classList.contains('open');
  }

  toggle(force?: boolean): void {
    const open = force ?? !this.open;
    this.el.classList.toggle('open', open);
    document.body.classList.toggle('drawer-open', open);
    if (open) {
      this.viewId = this.player.playlist?.id ?? this.lib.playlists[0]?.id ?? '';
      this.render();
    }
  }

  render(): void {
    if (!this.viewId) this.viewId = this.player.playlist?.id ?? this.lib.playlists[0]?.id ?? '';
    this.tabs.innerHTML = '';
    for (const pl of this.lib.playlists) {
      const tab = h(
        'button',
        {
          class: `pl-tab ${pl.id === this.viewId ? 'on' : ''} ${pl.kind === 'debug' ? 'debug' : ''}`,
          role: 'tab',
          'aria-selected': String(pl.id === this.viewId),
          onclick: () => {
            this.viewId = pl.id;
            this.render();
          },
        },
        h('span', { class: 'pl-name' }, pl.title),
        h('span', { class: 'pl-count' }, String(pl.tracks.length)),
      );
      this.tabs.appendChild(tab);
    }
    const pl = this.lib.playlist(this.viewId);
    this.list.innerHTML = '';
    if (!pl) return;
    if (pl.subtitle) this.list.appendChild(h('li', { class: 'pl-sub' }, pl.subtitle));
    if (!pl.tracks.length) {
      this.list.appendChild(
        h(
          'li',
          { class: 'pl-empty' },
          pl.kind === 'catalog'
            ? '此播单尚未接入真实音频。把音频与时间轴放入 public/audio、public/timelines，并在 public/catalog/catalog.json 中登记即可（详见 README）。'
            : '暂无曲目。',
        ),
      );
    }
    pl.tracks.forEach((id, i) => {
      const t = this.lib.tracks.get(id);
      if (!t) return;
      const isCur = this.player.playlist?.id === pl.id && this.player.index === i;
      const playable = this.lib.isPlayable(t);
      this.list.appendChild(
        h(
          'li',
          {},
          h(
            'button',
            {
              class: `pl-item ${isCur ? 'cur' : ''} ${t.kind === 'test-tone' ? 'test' : ''}`,
              disabled: !playable,
              onclick: () => void this.player.select(pl.id, i, true),
            },
            h('span', { class: 'pl-idx' }, isCur ? '◉' : String(i + 1)),
            h('span', { class: 'pl-meta' }, h('span', { class: 'pl-t' }, t.title), t.artist ? h('span', { class: 'pl-a' }, t.artist) : null),
            h('span', { class: 'pl-dur' }, t.duration ? fmtTime(t.duration) : playable ? '' : '未接入'),
          ),
        ),
      );
    });
  }
}
